"use client";

import type { BlokProps } from "@trydig/storyblok";
import { useEffect, useRef, useState } from "react";
import BlokWrap from "@/components/bloks/BlokWrap";
import type { BlokEmbed } from "@/storyblok";
import styles from "./Embed.module.css";

interface BlokEmbedWithCode extends BlokEmbed {
	code: {
		code: string;
	};
}

const validateCodeBlock = (blok: BlokEmbed): blok is BlokEmbedWithCode => {
	return (
		typeof blok.code === "object" &&
		typeof (blok.code as Record<string, any>)?.code === "string"
	);
};

export const EmbedBlok = ({ blok }: BlokProps<BlokEmbed>) => {
	const containerRef = useRef<HTMLDivElement>(null);
	const [sanitizedHtml, setSanitizedHtml] = useState("");

	if (!validateCodeBlock(blok)) {
		return null;
	}

	const embedCode = blok.code.code;

	useEffect(() => {
		const parser = new DOMParser();
		const doc = parser.parseFromString(embedCode, "text/html");
		const scripts = Array.from(doc.querySelectorAll("script"));
		const addedScripts: HTMLScriptElement[] = [];

		scripts.forEach((script) => script.remove());
		setSanitizedHtml(doc.body.innerHTML);

		for (const scriptElement of scripts) {
			const newScript = document.createElement("script");

			Array.from(scriptElement.attributes).forEach((attr) => {
				newScript.setAttribute(attr.name, attr.value);
			});

			if (scriptElement.textContent) {
				newScript.textContent = scriptElement.textContent;
			}

			document.body.appendChild(newScript);
			addedScripts.push(newScript);
		}

		return () => {
			for (const script of addedScripts) {
				script.remove();
			}
		};
	}, [embedCode]);

	return (
		<BlokWrap
			editable={blok}
			as="div"
			className={styles.root}
			ref={containerRef}
			dangerouslySetInnerHTML={{
				__html: sanitizedHtml,
			}}
		/>
	);
};
